


















































import { ResizeService } from '@/services/ResizeService'
import { Component, Emit, Prop, Vue } from 'vue-property-decorator'

// iOS Safari canvas hard limit is ~16M pixels (~4096x4096). Images larger than
// this either fail to render or get silently squashed. Keep a safety margin.
const MAX_MP = 4e6;
const JPG_COMPRESSION_QUALITY = 0.85; //between 0 and 1, default = 0.92

function loadImage(src: string): Promise<HTMLImageElement>
{
  return new Promise((resolve, reject) => {
    let img = document.createElement("img");
    // Honor EXIF orientation (iOS photos are typically rotated via EXIF).
    // Supported in Safari 13.1+, Chrome 81+, Firefox 77+.
    (img.style as any).imageOrientation = "from-image";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob>
{
  return new Promise((resolve, reject) => {
    if(typeof canvas.toBlob === "function")
    {
      canvas.toBlob(b => {
        if(b == null)
          reject(new Error("canvas.toBlob returned null"));
        else
          resolve(b);
      }, type, quality);
    }
    else
    {
      // Fallback for very old browsers without toBlob.
      try
      {
        let dataUrl = canvas.toDataURL(type, quality);
        let parts = dataUrl.split(",");
        let bin = atob(parts[1]);
        let arr = new Uint8Array(bin.length);
        for(let i = 0; i < bin.length; i++)
          arr[i] = bin.charCodeAt(i);
        resolve(new Blob([arr], { type }));
      }
      catch(err)
      {
        reject(err);
      }
    }
  });
}

async function readFile(file: File): Promise<HTMLImageElement>
{
  // Use createObjectURL instead of FileReader+dataURL to avoid keeping a
  // large base64 string in memory (iOS Safari frequently crashes/freezes
  // when handling several MB-sized data URLs).
  let srcUrl = URL.createObjectURL(file);
  let img: HTMLImageElement;
  try
  {
    img = await loadImage(srcUrl);
  }
  finally
  {
    // The pixel data has been decoded into the img; the source URL can be released.
    URL.revokeObjectURL(srcUrl);
  }

  let width = img.naturalWidth || img.width;
  let height = img.naturalHeight || img.height;
  let mp = width * height;

  if(mp > MAX_MP)
  {
    let factor = Math.sqrt(MAX_MP / mp);
    width = Math.round(width * factor);
    height = Math.round(height * factor);
  }

  let canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  let ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  let blob = await canvasToBlob(canvas, "image/jpeg", JPG_COMPRESSION_QUALITY);

  // Free the canvas backing store eagerly (helps on iOS Safari).
  canvas.width = 0;
  canvas.height = 0;

  let blobUrl = URL.createObjectURL(blob);
  let result = document.createElement("img");
  result.src = blobUrl;
  // Wait for the result image to actually decode so the caller can render it
  // immediately without flashing.
  try
  {
    if(typeof (result as any).decode === "function")
      await (result as any).decode();
  }
  catch
  {
    // decode() can reject on some browsers; ignore — the <img> will still load.
  }
  return result;
}

export interface IImageFile
{
  element: HTMLElement,
  filename: string,
  id?: any;
}

@Component
export default class FileUpload extends Vue
{
  @Prop({ default: false })
  disabled: boolean;

  active = false;
  get isMobile()  {
    return ResizeService.isMobileView;
  }

  @Emit("file")
  emitFile(e: IImageFile)
  {
  }

  onDropdownClick()
  {
    if(this.disabled !== true)
    {
      this.active = !this.active;
    }
  }

  onChange(ev)
  {
    let promises: Promise<any>[] = [];
    for(let f of ev.target.files)
    {
      let p = readFile(f).then(e => {
        this.emitFile({
          element: e,
          filename: f.name
        });
      });
      promises.push(p);
    }
    Promise.all(promises).then(e => {
      ev.target.value = null;
    })
    this.active = false;
  }
}
